import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Truck, MapPin, PackageCheck, PackageSearch, CheckCircle2 } from "lucide-react";

function fmt(n) {
    return new Intl.NumberFormat("en-KE", { maximumFractionDigits: 0 }).format(n || 0);
}

const ICON = { confirmed: PackageSearch, picking: PackageCheck, dispatched: Truck, delivered: CheckCircle2 };

export default function LogisticsTab() {
    const [orders, setOrders] = useState([]);
    const [selected, setSelected] = useState(null);
    const [tracking, setTracking] = useState(null);

    useEffect(() => {
        api.get("/procurement/orders").then((r) => {
            setOrders(r.data);
            if (r.data[0]) load(r.data[0].id);
        });
    }, []);

    const load = async (id) => {
        const { data } = await api.get(`/logistics/orders/${id}`);
        setTracking(data);
        setSelected(id);
    };

    return (
        <div className="space-y-6" data-testid="logistics-page">
            <div>
                <h2 className="font-display text-3xl font-extrabold text-onego-navy">Logistics tracking</h2>
                <p className="text-slate-600">Real-time order status across collection points and last-mile delivery.</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="og-card p-5">
                    <h3 className="font-display text-lg font-bold text-onego-navy">Your orders</h3>
                    <div className="mt-3 space-y-2 max-h-[420px] overflow-auto">
                        {orders.length === 0 && <p className="text-sm text-slate-500">No orders yet.</p>}
                        {orders.map((o) => (
                            <button
                                key={o.id}
                                onClick={() => load(o.id)}
                                className={`w-full text-left p-3 rounded-2xl border transition ${selected === o.id ? "border-pink-500 bg-pink-50/30" : "border-slate-200 hover:border-onego-navy"}`}
                                data-testid={`logistics-order-${o.id}`}
                            >
                                <p className="font-bold text-onego-navy text-sm">{o.items?.length || 0} items &middot; KES {fmt(o.total)}</p>
                                <p className="text-xs text-slate-500">{new Date(o.created_at).toLocaleString()}</p>
                            </button>
                        ))}
                    </div>
                </div>

                <div className="lg:col-span-2 og-card p-7">
                    {!tracking && <p className="text-sm text-slate-500">Select an order to view tracking.</p>}
                    {tracking && (
                        <>
                            <div className="flex items-center justify-between flex-wrap gap-3">
                                <div>
                                    <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Order</p>
                                    <p className="font-display text-2xl font-extrabold text-onego-navy">KES {fmt(tracking.order.total)}</p>
                                </div>
                                <span className="og-chip og-gradient text-white">{tracking.current_status.toUpperCase()}</span>
                            </div>
                            <div className="og-divider-gradient my-5" />
                            <ol className="space-y-5">
                                {tracking.timeline.map((s, i) => {
                                    const Icon = ICON[s.code] || MapPin;
                                    const active = i <= tracking.current_index;
                                    return (
                                        <li key={s.code} className="flex items-start gap-4" data-testid={`logistics-step-${s.code}`}>
                                            <div className={`h-10 w-10 rounded-full flex items-center justify-center ${active ? "og-gradient text-white" : "bg-slate-100 text-slate-400"}`}>
                                                <Icon size={18} />
                                            </div>
                                            <div>
                                                <p className={`font-bold ${active ? "text-onego-navy" : "text-slate-500"}`}>{s.label}</p>
                                                <p className="text-xs text-slate-500">{new Date(s.at).toLocaleString()}</p>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ol>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
